import { HTMLMotionProps, motion, useSpring, useTransform } from "framer-motion";
import { List } from "immutable";
import React, { JSX, useEffect, useState } from "react";
import { ResizePayload, useResizeDetector } from "react-resize-detector";
import styled, { css } from "styled-components";

interface SlidingContainerProps extends HTMLMotionProps<"div"> {
	elements: JSX.Element[];
	currentIndex: number;
}
export const SlidingContainer: React.FC<SlidingContainerProps> = ({ elements, currentIndex, ...htmlProps }) => {
	const animatedIndex = useSpring(currentIndex);

	const [heights, setHeights] = useState<List<number>>(List());

	useEffect(() => {
		animatedIndex.set(currentIndex);
	}, [currentIndex]);

	const percentageOffset = useTransform(animatedIndex, (value) => `-${value * 100}%`);

	return (
		<Container layout="position" {...htmlProps} h={heights.get(currentIndex)}>
			<motion.div style={{ x: percentageOffset }}>
				{elements.map((el, i) => (
					<SlidingContainerItem key={i} index={i} setHeight={(height) => setHeights(heights.set(i, height))}>
						{el}
					</SlidingContainerItem>
				))}
			</motion.div>
		</Container>
	);
};

interface SlidingContainerItemProps {
	index: number;
	setHeight: (height: number) => void;
	children: JSX.Element;
}
const SlidingContainerItem: React.FC<SlidingContainerItemProps> = ({ children, index, setHeight }) => {
	const { ref } = useResizeDetector({
		handleWidth: false,
		onResize: (payload: ResizePayload) => {
			if (payload.height) {
				setTimeout(() => setHeight(payload.height), 0);
			}
		},
	});

	return (
		<Tab offset={index} ref={ref}>
			{children}
		</Tab>
	);
};

const Container = styled(motion.div).withConfig({
	shouldForwardProp: (prop) => prop !== "h",
})<{ h?: number }>`
	position: relative;
	overflow: hidden;
	${({ h }) => h && `height: ${h}px`};
`;

const Tab = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "offset",
})<{ offset: number }>`
	${({ offset }) =>
		offset &&
		css`
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			transform: translateX(${offset * 100}%);
		`}
`;
